import React from 'react';
import PropTypes from 'prop-types';
import { 
  Card as MuiCard, 
  CardContent, 
  CardHeader, 
  CardActions, 
  CardMedia,
  Typography,
  Box,
  Divider
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const hoverScale = keyframes`
  to {
    transform: translateY(-4px);
  }
`;

// Styled components
const StyledCard = styled(MuiCard, {
  shouldForwardProp: (prop) => prop !== 'interactive' && prop !== 'variant' && prop !== 'elevation'
})(({ theme, interactive, variant, elevation = 0 }) => ({
  position: 'relative',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
  animation: `${fadeIn} 0.5s ease-out`,
  transition: 'all 0.3s ease',
  boxShadow: elevation ? theme.shadows[elevation] : '0 4px 12px rgba(0, 0, 0, 0.05)',
  border: variant === 'outlined' ? `1px solid ${theme.palette.divider}` : 'none',
  ...(interactive && {
    cursor: 'pointer',
    '&:hover': {
      animation: `${hoverScale} 0.3s forwards`,
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
    },
  }),
}));

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  padding: theme.spacing(2, 3),
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  flexGrow: 1,
}));

const StyledCardActions = styled(CardActions)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  justifyContent: 'flex-end',
}));

const StyledCardMedia = styled(CardMedia)(() => ({
  position: 'relative',
}));

const MediaOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(1, 2),
  background: alpha(theme.palette.common.black, 0.6),
  color: theme.palette.common.white,
}));

/**
 * Base Card component that can be used for various card types throughout the application
 */
const Card = ({
  title,
  subheader,
  avatar,
  action,
  media,
  mediaHeight = 200,
  mediaAlt = '',
  mediaOverlay,
  children,
  footer,
  variant = 'elevation',
  elevation = 0,
  interactive = false,
  onClick,
  className,
  ...props
}) => {
  return (
    <StyledCard 
      variant={variant} 
      elevation={elevation} 
      interactive={interactive} 
      onClick={interactive ? onClick : undefined}
      className={className}
      {...props}
    >
      {(title || subheader || avatar || action) && (
        <StyledCardHeader
          title={title}
          subheader={subheader}
          avatar={avatar}
          action={action}
        />
      )}
      
      {media && (
        <StyledCardMedia
          component="img"
          height={mediaHeight}
          image={media}
          alt={mediaAlt}
        >
          {mediaOverlay && (
            <MediaOverlay>
              {typeof mediaOverlay === 'string' ? (
                <Typography variant="body2">{mediaOverlay}</Typography>
              ) : (
                mediaOverlay
              )}
            </MediaOverlay>
          )}
        </StyledCardMedia>
      )}
      
      <StyledCardContent>
        {children}
      </StyledCardContent>
      
      {footer && (
        <>
          <Divider />
          <StyledCardActions>
            {footer}
          </StyledCardActions>
        </>
      )}
    </StyledCard>
  );
};

Card.propTypes = {
  title: PropTypes.node,
  subheader: PropTypes.node,
  avatar: PropTypes.node,
  action: PropTypes.node,
  media: PropTypes.string,
  mediaHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  mediaAlt: PropTypes.string,
  mediaOverlay: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  variant: PropTypes.oneOf(['elevation', 'outlined']),
  elevation: PropTypes.number,
  interactive: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

export default Card;
